import { de } from '../../i18n/de';
import { PBKDF2_ITERATIONS, deriveBackupKey } from './passphrase';

/**
 * The backup envelope: compress, encrypt, wrap in something a human can still identify.
 *
 * This is the **one irreversible decision in Phase 4**. Once a backup has been written with this format
 * it has to stay readable, or the thing the phase exists to protect is what gets lost. So the version is
 * in the file from the first byte, and an unknown version is refused rather than guessed at.
 *
 * ── Why a JSON wrapper and not raw bytes ──
 *
 * The header is readable. Somebody holding this file in five years can see what it is, which scheme
 * encrypted it, and when — without the passphrase, and without this source code. That is the same
 * argument that made the plaintext export "deliberately boring", and it costs 33 % on the ciphertext for
 * base64. Measured, that is 81 KB → 108 KB for a year of use, against a 1.68 MB uncompressed export: the
 * compression pays for the encoding twenty times over.
 *
 * ── Compress, then encrypt ──
 *
 * In that order, always. Encrypted bytes are incompressible, so the reverse order would upload 1.68 MB a
 * day forever. (The usual objection to compress-then-encrypt is that compression ratio leaks something
 * about the plaintext. Here the plaintext is one person's phrase bank, its approximate size is not a
 * secret, and there is no attacker-chosen content to exploit — the CRIME-style attacks need a request
 * loop this has no equivalent of.)
 *
 * ── The header is authenticated, not merely present ──
 *
 * The header travels in the clear and the server can see it. It is fed to AES-GCM as additional data, so
 * changing `createdAt` or `schemaVersion` in transit breaks decryption instead of quietly succeeding.
 * Without that, someone with write access to the container could relabel an old backup as the newest one
 * and a restore would take it.
 *
 * ── What stays plaintext ──
 *
 * The **local file export is not encrypted and must not become so.** `restore-runbook.md` rests on
 * somebody being able to open it in a text editor years from now to get a person their voice back. This
 * envelope is for the copy that leaves the device.
 */

export const BACKUP_FORMAT = 'sags-backup' as const;

/**
 * Envelope version 1: gzip → AES-GCM-256, PBKDF2-SHA256 key, base64 in JSON.
 *
 * Bump only for a change that makes an older file unreadable by the new code. Adding an optional header
 * field does not need a bump; changing the cipher, the KDF, the compression or the AAD does.
 */
export const ENVELOPE_VERSION = 1;

const IV_BYTES = 12;

/**
 * Field delimiter for the authenticated header, as an escape rather than a literal control character —
 * a raw `0x1f` in the source makes the whole file binary to `grep`, which is its own kind of bug.
 *
 * A delimiter rather than plain concatenation because concatenation is ambiguous: `salt:"AB" iv:"CD"` and
 * `salt:"ABC" iv:"D"` would produce identical additional data, and two different headers authenticating
 * to the same value is exactly the hole the AAD exists to close. `0x1f` (unit separator) cannot occur in
 * any header field — they are all base64, ISO timestamps, integers or fixed names.
 */
const FIELD_SEPARATOR = '\u001f';

export interface EnvelopeHeader {
  format: typeof BACKUP_FORMAT;
  envelope: number;
  /** Of the export inside, not of the envelope. Lets a restore refuse a file from a newer app. */
  schemaVersion: number;
  createdAt: string;
  compression: 'gzip';
  kdf: { name: 'PBKDF2'; hash: 'SHA-256'; iterations: number; salt: string };
  cipher: { name: 'AES-GCM'; iv: string };
}

export interface BackupEnvelope extends EnvelopeHeader {
  ciphertext: string;
}

export class BackupError extends Error {
  /** German, because a caregiver reads it (INV-05). */
  constructor(message: string) {
    super(message);
    this.name = 'BackupError';
  }
}

export interface SealInput {
  /** The plaintext export document, exactly as `serialiseExport` would write it to a file. */
  plaintext: string;
  key: CryptoKey;
  salt: Uint8Array<ArrayBuffer>;
  schemaVersion: number;
  createdAt: string;
  iterations?: number;
}

export async function sealBackup(input: SealInput): Promise<BackupEnvelope> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));

  const header: EnvelopeHeader = {
    format: BACKUP_FORMAT,
    envelope: ENVELOPE_VERSION,
    schemaVersion: input.schemaVersion,
    createdAt: input.createdAt,
    compression: 'gzip',
    kdf: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations: input.iterations ?? PBKDF2_ITERATIONS,
      salt: toBase64(input.salt),
    },
    cipher: { name: 'AES-GCM', iv: toBase64(iv) },
  };

  const compressed = await gzip(new TextEncoder().encode(input.plaintext));
  const sealed = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad(header) },
    input.key,
    compressed,
  );

  return { ...header, ciphertext: toBase64(new Uint8Array(sealed)) };
}

/**
 * Open an envelope with a typed passphrase.
 *
 * The key is always re-derived from the passphrase and the envelope's own salt, never taken from local
 * storage — a restore has to work on a device that has never seen this backup before, and having one code
 * path means the common case is the tested case.
 */
export async function openBackup(envelopeJson: string, passphrase: string): Promise<string> {
  const envelope = parseEnvelope(envelopeJson);

  const key = await deriveBackupKey(
    passphrase,
    fromBase64(envelope.kdf.salt),
    envelope.kdf.iterations,
  );

  const { ciphertext: _ciphertext, ...header } = envelope;

  let compressed: ArrayBuffer;
  try {
    compressed = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(envelope.cipher.iv), additionalData: aad(header) },
      key,
      fromBase64(envelope.ciphertext),
    );
  } catch {
    // AES-GCM cannot distinguish a wrong key from tampering, and neither can we. The message names the
    // cause a caregiver can actually act on.
    throw new BackupError(de.backupWrongPassphrase);
  }

  return new TextDecoder().decode(await gunzip(new Uint8Array(compressed)));
}

/**
 * Validate the untrusted wrapper before touching any crypto.
 *
 * Structural only, and deliberately strict about the version: a file from a **newer** envelope is refused
 * with its own message, because "wrong passphrase" would send somebody hunting for a card that was never
 * the problem.
 */
export function parseEnvelope(json: string): BackupEnvelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new BackupError(de.backupInvalid);
  }

  if (!parsed || typeof parsed !== 'object') throw new BackupError(de.backupInvalid);
  const candidate = parsed as Partial<BackupEnvelope>;

  if (candidate.format !== BACKUP_FORMAT) throw new BackupError(de.backupInvalid);
  if (typeof candidate.envelope !== 'number') throw new BackupError(de.backupInvalid);
  if (candidate.envelope > ENVELOPE_VERSION) throw new BackupError(de.backupEnvelopeNewer);
  if (candidate.envelope < ENVELOPE_VERSION) throw new BackupError(de.backupEnvelopeOlder);
  if (candidate.compression !== 'gzip') throw new BackupError(de.backupInvalid);
  if (typeof candidate.ciphertext !== 'string') throw new BackupError(de.backupInvalid);
  if (typeof candidate.schemaVersion !== 'number') throw new BackupError(de.backupInvalid);
  if (typeof candidate.createdAt !== 'string') throw new BackupError(de.backupInvalid);

  const kdf = candidate.kdf;
  if (
    !kdf ||
    kdf.name !== 'PBKDF2' ||
    kdf.hash !== 'SHA-256' ||
    typeof kdf.iterations !== 'number' ||
    typeof kdf.salt !== 'string'
  ) {
    throw new BackupError(de.backupInvalid);
  }

  const cipher = candidate.cipher;
  if (!cipher || cipher.name !== 'AES-GCM' || typeof cipher.iv !== 'string') {
    throw new BackupError(de.backupInvalid);
  }

  return candidate as BackupEnvelope;
}

export function serialiseEnvelope(envelope: BackupEnvelope): Blob {
  // Two-space indent, like the plaintext export: the header is meant to be read.
  return new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
}

/**
 * The additional authenticated data: the header, canonically ordered.
 *
 * Key order matters because this string is reconstructed at decryption time, so it is built field by
 * field rather than by `JSON.stringify` over an object whose key order is an accident of construction.
 */
function aad(header: EnvelopeHeader): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(
    [
      header.format,
      header.envelope,
      header.schemaVersion,
      header.createdAt,
      header.compression,
      header.kdf.name,
      header.kdf.hash,
      header.kdf.iterations,
      header.kdf.salt,
      header.cipher.name,
      header.cipher.iv,
    ].join(FIELD_SEPARATOR),
  );
}

async function gzip(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  return through(bytes, new CompressionStream('gzip'));
}

async function gunzip(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  try {
    return await through(bytes, new DecompressionStream('gzip'));
  } catch {
    // Only reachable if the ciphertext authenticated but the plaintext is not gzip — a version confusion
    // rather than a wrong passphrase, so it must not claim to be one.
    throw new BackupError(de.backupInvalid);
  }
}

async function through(
  bytes: Uint8Array<ArrayBuffer>,
  transform: GenericTransformStream,
): Promise<Uint8Array<ArrayBuffer>> {
  const stream = new Blob([bytes]).stream().pipeThrough(transform);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}
